import React, { useState, useEffect } from 'react';
import { backtestApi } from '../../services/api';
import './AIInsights.css';

interface AIInsightsProps {
  backtestId: number | null;
  cachedInsights: { insights: string; strategyName: string } | null;
  onInsightsFetched: (insights: { insights: string; strategyName: string }) => void;
  dateRange?: string;
}

const AIInsights: React.FC<AIInsightsProps> = ({ backtestId, cachedInsights, onInsightsFetched, dateRange }) => {
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState<string | null>(cachedInsights?.insights || null);
  const [error, setError] = useState<string | null>(null);
  const [strategyName, setStrategyName] = useState<string>(cachedInsights?.strategyName || '');

  const fetchInsights = async () => {
    if (!backtestId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await backtestApi.getAIInsights(backtestId);
      setInsights(response.insights);
      setStrategyName(response.strategyName);
      // Persist insights to parent component
      onInsightsFetched(response);
    } catch (err: any) {
      console.error('Error fetching AI insights:', err);
      setError(err.response?.data?.message || 'Failed to get AI insights. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Use cached insights if available
    if (cachedInsights) {
      setInsights(cachedInsights.insights);
      setStrategyName(cachedInsights.strategyName);
    }
    // DO NOT auto-fetch - only fetch when user clicks "Load Backtest" or "Refresh Insights"
  }, [cachedInsights]);

  if (!backtestId) {
    return (
      <>
        <div className="insights-header">
          <h3>AI Insights</h3>
        </div>
        <div className="no-data">
          Run or load a backtest to get AI analysis.
        </div>
      </>
    );
  }

  return (
    <>
      <div className="insights-header">
        <h3>AI Insights{dateRange}</h3>
      </div>

      {loading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Analyzing backtest with OpenAI GPT-4o...</p>
          <p className="loading-note">This may take 10-20 seconds</p>
        </div>
      )}

      {error && (
        <div className="error-state">
          <h4>Error</h4>
          <p>{error}</p>
          <button onClick={fetchInsights} className="retry-button">
            Try Again
          </button>
        </div>
      )}

      {insights && !loading && (
        <div className="insights-content">
          <div className="insights-text">
            {insights.split('\n').map((line, index) => {
              // Check if line is a header (starts with ##)
              if (line.startsWith('## ')) {
                return <h4 key={index} className="insight-section-title">{line.replace('## ', '')}</h4>;
              }
              // Check if line is a numbered list item
              else if (line.match(/^\d+\./)) {
                return <p key={index} className="numbered-item">{line}</p>;
              }
              // Check if line is a bullet point
              else if (line.startsWith('- ')) {
                return <p key={index} className="bullet-item">{line}</p>;
              }
              // Check if line is bold text (starts with **)
              else if (line.startsWith('**')) {
                return <p key={index} className="bold-text">{line.replace(/\*\*/g, '')}</p>;
              }
              // Regular line
              else if (line.trim()) {
                return <p key={index}>{line}</p>;
              }
              // Empty line for spacing
              return <div key={index} className="line-break"></div>;
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default AIInsights;
